import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Box, Button, Container, MenuItem, TextField } from '@mui/material';
import ConfirmModal from '../../_common/Modal/ConfirmModal';
import BasicInfo from '../../_common/BasicInfo/BasicInfo';
import CategoryCard from './components/CategoryCard';
import { AppDispatch, RootState } from '../../stores/store';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchInspectionTop,
  setSelectedCategoryId,
  updateInspectionTop,
} from '../../stores/inspectionTop/slice';
import { useTranslation } from 'react-i18next';
import { fetchJudgementData } from '../../stores/judgement/slice';
import { INSPEX_PATHNAME } from '../../_constants/Const';
import {
  fetchQcTypeDataAll,
  updateInspectionTypeData,
  updateIsAllOk,
  updateJudgement,
} from '../../stores/inspectionTypeData/slice';
import { isInspection } from '../../_utils/utils';
import {
  HISTORY_STATUSES,
  HISTORY_TYPE,
  INSPECTION_STATUSES,
  JUDGEMENT,
} from '../../_constants/inspectionConst';
import { getStringBtnFinishTest } from '../../_utils/inspectionUtils';
import { InspectionType } from '../../services/apiTypes';
import { PROGRESS_CARD_GAP } from '../../_constants/styleConst';
import useErrorModal from '../../hooks/useErrorModal';
import ErrorModal from '../../_common/Modal/ErrorModal';
import {
  isGetInspectionTop,
  isGetQcTypeList,
  isJudgementData,
} from '../../_utils/typeGuardUtils';
import { isFulfilled, isRejected } from '@reduxjs/toolkit';
import { API_CODE } from '../../_constants/apiConst';
import { useNavigate } from 'react-router-dom';
import useUpdateStatus from '../../hooks/useUpdateStatus';
import useSaveHistory from '../../hooks/useSaveHistory';

const InspectionTop: React.FC = () => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { error, showApiError, showTypeError, showClientError, hideError } =
    useErrorModal();
  const updateStatus = useUpdateStatus();
  const saveHistory = useSaveHistory();
  const { repairRequest } = useSelector(
    (state: RootState) => state.repairRequest,
  );
  const { inspectionTop } = useSelector(
    (state: RootState) => state.inspectionTop,
  );
  const { inspectionTypeDataMap, before, isAllOk } = useSelector(
    (state: RootState) => state.inspectionTypeData,
  );

  const [isOpenFinishModal, setIsOpenFinishModal] = useState(false);
  // const [isAllOk, setIsAllOk] = useState(false);
  /** 全ての診断が完了しているか */
  const [isAllComplete, setIsAllComplete] = useState(
    inspectionTop.inspectionCategoryList.length > 0 &&
      inspectionTop.inspectionCategoryList.every(
        (category) => category.progress === 100,
      ),
  );

  useEffect(() => {
    if (repairRequest) {
      fetchTopData(repairRequest.rmaNo, isInspection(repairRequest.status));
    }
  }, [repairRequest]);

  useEffect(() => {
    (async () => {
      const result =
        inspectionTop.inspectionCategoryList.length > 0 &&
        inspectionTop.inspectionCategoryList.every(
          (category) => category.progress === 100,
        );
      setIsAllComplete(result);
      await getQcAllAndCheckAllOk(result);
    })();
  }, [inspectionTop]);

  const getQcAllAndCheckAllOk = async (
    isAllCompleteFlag: boolean = isAllComplete,
  ) => {
    if (
      isAllCompleteFlag &&
      repairRequest &&
      !isInspection(repairRequest.status)
    ) {
      if (
        Object.keys(inspectionTypeDataMap).length === 0 ||
        Object.keys(before).length === 0
      ) {
        const response = await dispatch(
          fetchQcTypeDataAll({ rmaNo: repairRequest.rmaNo }),
        );
        if (isFulfilled(response)) {
          if (!isGetQcTypeList(response.payload)) {
            showTypeError(API_CODE.QC_ALL);
          } else {
            dispatch(updateIsAllOk());
          }
        } else {
          showApiError(response.payload, API_CODE.QC_ALL, () =>
            navigate(INSPEX_PATHNAME.RMA_SEARCH),
          );
        }
      }
    }
  };

  // 診断Topデータを取得
  const fetchTopData = async (rmaNo: string, isInspection: boolean) => {
    const response = await dispatch(
      fetchInspectionTop({ query: { rmaNo }, isInspection }),
    );

    const apiCode = isInspection ? API_CODE.INSPECTION_TOP : API_CODE.QC_TOP;
    if (isRejected(response)) {
      showApiError(response.payload, apiCode, () =>
        navigate(INSPEX_PATHNAME.RMA_SEARCH),
      );
      return;
    } else if (!isGetInspectionTop(response.payload)) {
      showTypeError(apiCode);
      return;
    }
  };

  /** judgementの更新APIの呼び出し&storeのjudgementを更新 */
  const updateQcTypeData = async (newInspectionType: InspectionType) => {
    const response = await dispatch(
      updateInspectionTypeData({
        newInspectionType: newInspectionType,
        isInspection: false,
      }),
    );

    if (isRejected(response)) {
      showApiError(response.payload, API_CODE.QC_TYPE);
      return false;
    }

    newInspectionType.itemData.forEach((item) => {
      dispatch(
        updateJudgement({
          categoryId: newInspectionType.categoryId,
          typeId: newInspectionType.typeId,
          path: [item.id],
          judgement: item.judgement,
        }),
      );
      if (item.itemData.length > 0) {
        item.itemData.forEach((problem) => {
          dispatch(
            updateJudgement({
              categoryId: newInspectionType.categoryId,
              typeId: newInspectionType.typeId,
              path: [item.id, problem.id],
              judgement: problem.judgement,
            }),
          );

          if (problem.itemData.length > 0) {
            problem.itemData.forEach((problemSub) => {
              dispatch(
                updateJudgement({
                  categoryId: newInspectionType.categoryId,
                  typeId: newInspectionType.typeId,
                  path: [item.id, problem.id, problemSub.id],
                  judgement: problemSub.judgement,
                }),
              );
            });
          }
        });
      }
    });
    return true;
  };

  /** 出荷検査で全てのjudgementをOKにしてPUT */
  const updateInspectionQcValueAllOk = async () => {
    if (
      !repairRequest ||
      Object.keys(inspectionTypeDataMap).length === 0 ||
      Object.keys(before).length === 0 ||
      isInspection(repairRequest.status)
    ) {
      return false;
    }
    let promiseAllResult = true;
    await Promise.all(
      inspectionTop.inspectionCategoryList.map(async (category) => {
        for (const [, afterTypeData] of Object.entries(
          inspectionTypeDataMap[category.id],
        )) {
          const updateData = {
            ...afterTypeData,
            itemData: afterTypeData.itemData.map((item) => {
              if (!item.itemData || item.itemData.length === 0) {
                return { ...item, judgement: JUDGEMENT.OK };
              } else {
                return {
                  ...item,
                  judgement: JUDGEMENT.OK,
                  itemData: item.itemData.map((problem) => {
                    if (!problem.itemData || problem.itemData.length === 0) {
                      return { ...problem, judgement: JUDGEMENT.OK };
                    } else {
                      return {
                        ...problem,
                        judgement: JUDGEMENT.OK,
                        itemData: problem.itemData.map((problemSub) => {
                          return { ...problemSub, judgement: JUDGEMENT.OK };
                        }),
                      };
                    }
                  }),
                };
              }
            }),
          };
          if (!(await updateQcTypeData(updateData))) {
            promiseAllResult = false;
          }
        }
      }),
    );

    if (!promiseAllResult) {
      return false;
    }

    return true;
  };

  // 元々の状況に、エラーメッセージを表示しない
  const [isCategoryTouched, setIsCategoryTouched] = useState(false);

  const isCategoryError = useMemo(() => {
    return (
      inspectionTop.partsCategoryList.length > 1 &&
      inspectionTop.selectedCategoryId === ''
    );
  }, [inspectionTop]);

  /** 類別の変更 */
  const handlePartsCategoryChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    dispatch(setSelectedCategoryId(event.target.value));
  };

  const handleOpen = async () => {
    if (!repairRequest) {
      return;
    }
    if (!isInspection(repairRequest.status)) {
      setIsOpenFinishModal(true);
      return;
    }
    // 受入診断の場合、全てN/Aかどうかチェック
    const response = await dispatch(
      fetchJudgementData({ rmaNo: repairRequest.rmaNo }),
    );

    if (isFulfilled(response)) {
      if (!isJudgementData(response.payload)) {
        showTypeError(API_CODE.INSPECTION_JUDGEMENTS);
        return;
      }
    } else {
      showApiError(response.payload, API_CODE.INSPECTION_JUDGEMENTS);
      return;
    }

    const { empty, ok, ng } = response.payload;
    if (empty === 0 && ok === 0 && ng === 0) {
      showClientError('ERR_CONFIRM_ALL_NA');
      return;
    }
    setIsOpenFinishModal(true);
  };

  const onClickCancel = () => {
    setIsOpenFinishModal(false);
  };

  const onClickOk = async () => {
    setIsOpenFinishModal(false);
    if (!repairRequest) {
      return;
    }
    // 受入診断
    if (isInspection(repairRequest.status)) {
      // 類別が選択可の時は更新しておく
      if (inspectionTop.partsCategoryList.length > 1) {
        const isSuccessTop = await dispatch(
          updateInspectionTop({
            newInspectionTop: inspectionTop,
            isInspection: true,
          }),
        );
        if (isRejected(isSuccessTop)) {
          showApiError(isSuccessTop.payload, API_CODE.INSPECTION_TOP);
          return;
        }
      }
      // ステータス更新
      const isSuccess = await updateStatus(
        INSPECTION_STATUSES.INSPECTION_INPUT_RESULT,
        0,
        showApiError,
      );
      if (!isSuccess) {
        return;
      }

      /** 操作履歴(受入診断のCompletion)を保存 */
      saveHistory(
        repairRequest,
        HISTORY_STATUSES.INSPECTION.id,
        HISTORY_TYPE.COMPLETION.id,
        () => navigate(INSPEX_PATHNAME.INSPECTION_INPUT_RESULT),
        showApiError,
      );
      return;
    }
    //出荷検査
    if (!(await updateInspectionQcValueAllOk())) {
      return;
    }
    // ステータス更新
    const isSuccess = await updateStatus(
      // ExFMから登録され、1度もデータ連携していないか
      repairRequest.isCreatedByExFM && !repairRequest.linkedQcToExFM
        ? INSPECTION_STATUSES.QC_NOT_LINKED
        : INSPECTION_STATUSES.QC_COMPLETE,
      inspectionTop.qcCount,
      showApiError,
    );
    if (!isSuccess) {
      return;
    }

    /** 操作履歴(出荷検査のCompletion)を保存 */
    saveHistory(
      repairRequest,
      HISTORY_STATUSES.QC.id,
      HISTORY_TYPE.COMPLETION.id,
      () =>
        navigate(INSPEX_PATHNAME.REPORT, {
          state: {
            defaultQcCount: inspectionTop.qcCount,
          },
        }),
      showApiError,
    );
  };

  const onClickReport = () => {
    navigate(INSPEX_PATHNAME.REPORT, {
      state: {
        // 出荷検査1回目の場合は受入診断の結果を初期表示する
        defaultQcCount: inspectionTop.qcCount === 1 ? 0 : undefined,
      },
    });
  };

  return (
    <Box>
      <BasicInfo />
      {repairRequest && inspectionTop.rmaNo !== '' && (
        <Container sx={{ mt: '3%' }} maxWidth={'xl'}>
          {inspectionTop.partsCategoryList.length > 1 &&
            isInspection(repairRequest.status) && (
              <TextField
                select
                label={t('LBL_PARTS_CATEGORY')}
                value={inspectionTop.selectedCategoryId ?? '0'}
                onChange={handlePartsCategoryChange}
                size="small"
                sx={{ marginLeft: 3, width: '20%' }}
                onBlur={() => setIsCategoryTouched(true)}
                error={isCategoryError && isCategoryTouched}
                helperText={
                  isCategoryError && isCategoryTouched ? t('ERR_INPUT') : ''
                }
              >
                {inspectionTop.partsCategoryList.map((item, i) => (
                  <MenuItem key={`model_category_${i}`} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          <Box m={2} p={1} sx={{ flexGrow: 1 }}>
            <Box
              sx={{ display: 'flex', flexWrap: 'wrap', gap: PROGRESS_CARD_GAP }}
            >
              {[...inspectionTop.inspectionCategoryList]
                .sort((prev, next) => Number(prev.id) - Number(next.id))
                .map((item) => ({
                  ...item,
                  pathIcon: `/logo/icon_${item.id}.svg`,
                }))
                .map((inspectionCategory, i) => (
                  <CategoryCard
                    key={`category_card_${i}`}
                    rmaNo={inspectionTop.rmaNo}
                    isInspection={isInspection(repairRequest.status)}
                    id={inspectionCategory.id}
                    label={inspectionCategory.label}
                    progress={inspectionCategory.progress}
                    pathIcon={inspectionCategory.pathIcon}
                  />
                ))}
            </Box>
            {/* 出荷検査時のみ帳票ボタンを表示 */}
            {!isInspection(repairRequest.status) && (
              <Box sx={{ mt: '2%' }}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ width: `calc(25% - ${PROGRESS_CARD_GAP})` }}
                  onClick={onClickReport}
                >
                  {t('BTN_REPORT')}
                </Button>
              </Box>
            )}
            <Box sx={{ mt: '3%' }}>
              <Button
                variant="contained"
                color="primary"
                sx={{ width: `calc(100% - ${PROGRESS_CARD_GAP})` }}
                disabled={
                  repairRequest && isInspection(repairRequest.status)
                    ? !isAllComplete ||
                      inspectionTop.inspectionCategoryList.length === 0
                    : !(isAllComplete && isAllOk) ||
                      inspectionTop.inspectionCategoryList.length === 0
                }
                onClick={handleOpen}
              >
                {repairRequest &&
                  t(getStringBtnFinishTest(repairRequest.status))}
              </Button>
            </Box>
          </Box>
        </Container>
      )}
      <ConfirmModal
        isOpen={isOpenFinishModal}
        displayMessage={{
          messageKey: 'MSG_COMPLETE',
          values: { 0: t('LBL_INSPECTION') },
        }}
        onClickCancel={onClickCancel}
        onClickOk={onClickOk}
      />
      {error && <ErrorModal message={error} onClose={hideError} />}
    </Box>
  );
};

export default InspectionTop;
